/**
 * The hint for a studio server this browser cannot reach by its name (D16):
 * what is probably wrong and what to do — the browser's Local Network Access
 * permission, DNS rebinding protection on the router, an untrusted
 * certificate, no answer on the LAN, or another network
 * (`connectivity/reachHint.ts` decides which). Shown in the connectivity panel
 * and a site's server list.
 *
 * Presentational: the hint comes in as a prop; the one action — "Connect to
 * local servers", offered with a Local Network Access hint when the caller
 * passes `onConnectLocal` — goes out.
 */
import { TriangleAlertIcon, WifiIcon } from 'lucide-react';
import type { ReachHint } from '../connectivity/reachHint.js';
import { Button } from './ui/button.js';

export type ReachHintNoteProps = {
  readonly hint: ReachHint;
  /** Probe the local servers now, from this click (Chrome asks for the permission then); absent: no button here. */
  readonly onConnectLocal?: () => void;
};

export function ReachHintNote({ hint, onConnectLocal }: ReachHintNoteProps) {
  return (
    <div className="flex max-w-sm items-start gap-1.5 text-xs" data-testid="reach-hint" data-case={hint.case}>
      <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden="true" />
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-warning">{hint.title}</span>
        <span className="text-muted-foreground">{hint.detail}</span>
        <span className="text-muted-foreground">
          See “{hint.docs.section}” in the sites guide (<span className="font-mono">{hint.docs.path}</span>).
        </span>
        {hint.case === 'local-network' && onConnectLocal !== undefined ? (
          <Button size="sm" variant="outline" className="mt-1 self-start" onClick={onConnectLocal} data-testid="connect-local">
            <WifiIcon aria-hidden="true" />
            Connect to local servers
          </Button>
        ) : null}
      </div>
    </div>
  );
}
